import React, { useState, useEffect, useCallback } from "react";
import { Button, Card, Table, Badge, Form, Alert, Modal, Row, Col } from "react-bootstrap";
import axios from "axios";
import config from "../../../config";
import { toast } from "react-toastify";
import { useHasPermission, PERMISSIONS } from "../../../utils/permissions";
import PermissionGate from "../../common/PermissionGate";

const PaymentStatusBadge = ({ status }) => {
  const variants = {
    'Pending': 'secondary',
    'Processed': 'primary',
    'Completed': 'success',
    'Failed': 'danger',
    'Cancelled': 'danger'
  };
  return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
};

const CreatePaymentModal = ({ show, onHide, pmPurchaseOrderId, order, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    amount: 0,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: '',
    referenceNumber: '',
    remarks: ''
  });

  useEffect(() => {
    if (show && order) {
      setFormData(prev => ({
        ...prev,
        amount: order.totalAmount || 0
      }));
    }
  }, [show, order]);

  const handleSubmit = async () => {
    if (!formData.amount || !formData.paymentMethod || !formData.referenceNumber) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      const paymentPayload = {
        pmPurchaseOrder: pmPurchaseOrderId,
        ...formData,
        status: 'Pending'
      };

      await axios.post(`${config.API_BASE_URL}/pm-payments`, paymentPayload);
      toast.success("Payment recorded successfully");
      onHide();
      onUpdate();
    } catch (error) {
      console.error("Error creating payment:", error);
      toast.error("Failed to record payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Record Payment for PM PO #{order?.poNumber}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Amount <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) }))}
                  min="0"
                  step="0.01"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Payment Date <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="date"
                  value={formData.paymentDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, paymentDate: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Payment Method <span className="text-danger">*</span></Form.Label>
            <Form.Select
              value={formData.paymentMethod}
              onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))}
            >
              <option value="">Select Method</option>
              <option value="Cash">Cash</option>
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="Cheque">Cheque</option>
              <option value="UPI">UPI</option>
              <option value="Credit Card">Credit Card</option>
              <option value="Debit Card">Debit Card</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Reference Number <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              value={formData.referenceNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, referenceNumber: e.target.value }))}
              placeholder="Cheque number, UPI ref, etc."
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Remarks</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.remarks}
              onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
              placeholder="Additional payment notes"
            />
          </Form.Group>

          {order && (
            <Alert variant="info">
              <div className="d-flex justify-content-between">
                <span><strong>Order Total:</strong> ₹{parseFloat(order.totalAmount).toFixed(2)}</span>
                <span><strong>Payment Amount:</strong> ₹{parseFloat(formData.amount).toFixed(2)}</span>
              </div>
            </Alert>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Recording...' : 'Record Payment'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default function PMPaymentTab({ pmPurchaseOrderId, order }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const canCreatePayment = useHasPermission(PERMISSIONS.PM_PO_EDIT);
  const canViewPayments = useHasPermission(PERMISSIONS.PM_PO_VIEW);

  const fetchPayments = useCallback(async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/pm-payments?pmPurchaseOrder=${pmPurchaseOrderId}`);
      setPayments(response.data);
    } catch (error) {
      console.error("Error fetching payments:", error);
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  }, [pmPurchaseOrderId]);

  useEffect(() => {
    fetchPayments();
  }, [pmPurchaseOrderId, fetchPayments]);

  const handleCreatePayment = () => {
    if (!canCreatePayment) {
      toast.error("You don't have permission to record payments");
      return;
    }
    setShowPaymentModal(true);
  };

  // const calculateTotalPaid = () => {
  //   return payments
  //     .filter(payment => payment.status === 'Completed')
  //     .reduce((sum, payment) => sum + (payment.amount || 0), 0);
  // };

  // const calculateOutstanding = () => {
  //   const total = order?.totalAmount || 0;
  //   const paid = calculateTotalPaid();
  //   return total - paid;
  // };

  if (loading) {
    return <div className="text-center py-4">Loading payment records...</div>;
  }
  if (!canViewPayments) {
    return <div className="text-center py-4">Permission Denied</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="mb-1">
            <i className="fas fa-money-check me-2"></i>Payment Records
          </h5>
          <small className="text-muted">Track payments for this packaging material purchase order</small>
        </div>

        <PermissionGate require={PERMISSIONS.PM_PO_EDIT}>
          <Button
            variant="success"
            onClick={handleCreatePayment}
          >
            <i className="fas fa-plus me-2"></i>Record Payment
          </Button>
        </PermissionGate>
      </div>

      {/* Payment Summary */}
      {order && (
        <Row className="mb-4">
          <Col md={4}>
            <Card className="text-center">
              <Card.Body>
                <h6 className="text-muted">Order Total</h6>
                <h4 className="text-primary">₹{parseFloat(order.totalAmount).toFixed(2)}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="text-center">
              <Card.Body>
                <h6 className="text-muted">Total Paid</h6>
                <h4 className="text-success">₹{(order?.paidAmount || 0).toFixed(2)}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="text-center">
              <Card.Body>
                <h6 className="text-muted">Outstanding</h6>
                <h4 className={(order?.remainingAmount || 0) > 0 ? "text-danger" : "text-success"}>
                  ₹{(order?.remainingAmount || 0).toFixed(2)}
                </h4>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Payment Records */}
      {payments.length > 0 ? (
        <div className="table-responsive">
          <Table bordered hover>
            <thead className="table-light">
              <tr>
                <th>Payment Date</th>
                <th>Amount</th>
                <th>Payment Method</th>
                <th>Reference Number</th>
                <th>Status</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(payment => (
                <tr key={payment._id}>
                  <td>{new Date(payment.paymentDate).toLocaleDateString()}</td>
                  <td className="fw-bold">₹{parseFloat(payment.amount).toFixed(2)}</td>
                  <td>{payment.paymentMethod}</td>
                  <td className="text-muted">{payment.referenceNumber}</td>
                  <td><PaymentStatusBadge status={payment.status} /></td>
                  <td>{payment.remarks || '-'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-5 text-muted">
          <i className="fas fa-money-check fa-3x mb-3"></i>
          <h5>No Payment Records</h5>
          <p>Create your first payment record to track supplier payments.</p>
        </div>
      )}

      {/* Create Payment Modal */}
      <CreatePaymentModal
        show={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        pmPurchaseOrderId={pmPurchaseOrderId}
        order={order}
        onUpdate={fetchPayments}
      />
    </div>
  );
}
